"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { MapPin } from "lucide-react";
import OwnerInfoMobile from "./owner-info-mobile";
import HomeOwnersFilterIndicator from "./home-owners-filter-indicator";
import HomeOwnersResultsCount from "./home-owners-results-count";
import { buttonIcon } from "../../../shared/constants/images";
import { DASHBOARD_ROUTE } from "../../../shared/constants/pages-routes";
import { buildHomeownersDetailUrlWithReturn } from "../../../shared/lib/utils/navigation-helpers";
import { useFilterSearchHomes, FilterSearchHomesResponse } from "../hooks/filter-search-homes.hook";
import DeleteHomeownerDialog from "./delete-homeowner-dialog";
import { useFilterSearchExport } from "../context/filter-search-export-context";
import { useSession } from "@/features/auth/context/session.context";
import { UserRole } from "../../../shared/constants/user-roles";
import { useSearchParams } from "next/navigation";
import HomeOwnersTablePagination from "./home-owners-table-pagination";
import { usePaginationData } from "../context/pagination-data-context";
import { updateHomeReportedStatus } from "@/features/homeowners/api/homes.api";
import successToast from "../../../shared/lib/toast/success.toast";
import errorToast from "../../../shared/lib/toast/error.toast";
import { useQueryClient } from "@tanstack/react-query";
import { useRegionId } from "@/shared/hooks/use-region-id";
import { useRegionAwareHref } from "@/shared/hooks/use-region-aware-href";
import { useRegion } from "@/shared/hooks/use-region";
import { buildRegionAwareHref } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { getStatusButtonClass } from "../lib/status-button-colors";
import {
    displayOverallStatusLabel,
    normalizeOverallStatus,
    normalizeOverallStatusList,
    compactStatusLabelForHomeownersList,
} from "../lib/overall-status.utils";
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils";
import { getDisplayAddressValue, isUnknownLocationValue, searchableAddressValue } from "../lib/unknown-location";

// Helper function to format phone number for tel: link
function getPhoneTelLink(phone: string | null | undefined): string | null {
  if (!phone) return null
  // Keep only digits and + sign for tel: links
  return `tel:${phone.replace(/[^\d+]/g, '')}`
}

function buildMapLink(route: string, homeId?: number | null) {
  const [base, queryString] = route.split('?')
  const params = new URLSearchParams(queryString || '')
  if (homeId) {
    params.set('homeId', homeId.toString())
  } else {
    params.delete('homeId')
  }
  const query = params.toString()
  return query ? `${base}?${query}` : base
}

function ReportedStatusToggleSwitch({
  homeId,
  currentStatus,
  onStatusChange,
}: {
  homeId: number;
  currentStatus: boolean;
  onStatusChange?: (homeId: number, newStatus: boolean) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [localStatus, setLocalStatus] = useState(currentStatus);
  const [isOptimistic, setIsOptimistic] = useState(false);

  useEffect(() => {
    if (!isOptimistic) {
      setLocalStatus(currentStatus);
    }
  }, [currentStatus, isOptimistic]);

  const isActive = localStatus;

  const handleClick = async () => {
    if (!onStatusChange) return;
    setIsLoading(true);
    const newStatus = !isActive;
    setLocalStatus(newStatus);
    setIsOptimistic(true);
    try {
      await onStatusChange(homeId, newStatus);
    } catch (error) {
      console.error("Failed to update home reported status:", error);
      setLocalStatus(currentStatus);
      setIsOptimistic(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={isActive ? "default" : "outline"}
      size="sm"
      className="shrink-0"
      onClick={handleClick}
      disabled={isLoading}
      aria-label={isActive ? "Set inactive" : "Set active"}
    >
      {isActive ? "Active" : "Inactive"}
    </Button>
  );
}

type HomeOwnersCardsMobileProps = {
  hideOwnerDetails?: boolean;
  hideFlowchartLinks?: boolean;
  mapRoute?: string;
};

function HomeOwnersCardsMobile(props: HomeOwnersCardsMobileProps) {
  const { hideOwnerDetails = false, hideFlowchartLinks = false, mapRoute = DASHBOARD_ROUTE.map } = props;
  const { regionId, isLoading: regionLoading } = useRegionId()
  const { regionSlug } = useRegion()
  const searchParams = useSearchParams()
  const workscope = searchParams.get("workscope") || ""
  const workscopeStatus = searchParams.get("workscopeStatus") || ""
  const workscopeGoodwill = searchParams.get("workscopeGoodwill") ?? ""
  const workscopeGoodwillStatus = searchParams.get("workscopeGoodwillStatus") ?? ""
  // Normalize workscopeGoodwill to slugs (same as desktop) so API receives e.g. "exterior-debris-removal-goodwill"
  const workscopeGoodwillSlugs = useMemo(() => {
    if (!workscopeGoodwill.trim()) return []
    return workscopeGoodwill.split(',').map((s) => s.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')).filter(Boolean)
  }, [workscopeGoodwill])
  // Start fetching as soon as we have regionSlug (from URL); no need to wait for regionId.
  const shouldFetchHomes = (regionSlug != null && regionSlug.trim() !== '') || (!regionLoading && regionId != null)
  const { data: allHomesData, isLoading, error } = useFilterSearchHomes(
    regionId ?? undefined,
    true,
    {
      enabled: shouldFetchHomes,
      regionSlug: regionSlug ?? undefined,
      workscope: workscope || undefined,
      workscopeStatus: workscopeStatus || undefined,
      workscopeGoodwill: workscopeGoodwillSlugs.length > 0 ? workscopeGoodwillSlugs.join(',') : undefined,
      workscopeGoodwillStatus: workscopeGoodwillStatus.trim() || undefined,
      initialLimit: 50,
    }
  )
  const effectiveMapRoute = useRegionAwareHref(mapRoute)
  const { session } = useSession()
  const isVolunteer = session?.role === UserRole.VOLUNTEER
  const { setPaginationData } = usePaginationData();
  const { setFilteredHomesForExport } = useFilterSearchExport();
  const queryClient = useQueryClient();

  // Handle reported status change
  const handleReportedStatusChange = useCallback(
    async (homeId: number, newStatus: boolean) => {
      try {
        const result = await updateHomeReportedStatus(homeId, newStatus);
        if (result.success) {
          successToast(`Home reported status updated to ${newStatus ? 'Active' : 'Inactive'}`);
          await queryClient.invalidateQueries({ queryKey: ['homes', 'filter-search'] });
        } else {
          errorToast(
            result.message || "Failed to update home reported status"
          );
        }
      } catch (error) {
        errorToast("Failed to update home reported status");
        console.error("Error updating home reported status:", error);
      }
    },
    [queryClient]
  );

  // Get pagination and filter parameters
  const currentPage = parseInt(searchParams.get("page") || "1");
  const pageSize = 50; // Same as desktop
  const search = searchParams.get("search") || "";
  const overallStatus = searchParams.get("overallStatus") || "";
  const reliefStatus = searchParams.get("reliefStatus") || "";
  const rebuildingStatus = searchParams.get("rebuildingStatus") || "";
  const goodwillStatus = searchParams.get("goodwillStatus") || "";
  const roofing = searchParams.get("roofing") || "";
  const kindOfRoofWork = searchParams.get("kindOfRoofWork") || "";
  const medicalNeeds = isVolunteer ? "" : (searchParams.get("medicalNeeds") || "");
  const homeownerAge = isVolunteer ? "" : (searchParams.get("homeownerAge") || "");
  const isSingleParent = isVolunteer ? "" : (searchParams.get("isSingleParent") || "");
  const numChildrenUnder18 = isVolunteer ? "" : (searchParams.get("numChildrenUnder18") || "");
  const isFirstResponder = isVolunteer ? "" : (searchParams.get("isFirstResponder") || "");
  const isVeteran = isVolunteer ? "" : (searchParams.get("isVeteran") || "");
  const residenceType = searchParams.get("residenceType") || "";
  const structureType = searchParams.get("structureType") || "";
  const assessmentCompleted = searchParams.get("assessmentCompleted") || "";
  const updatedFrom = searchParams.get("updatedFrom") || "";
  const updatedTo = searchParams.get("updatedTo") || "";
  const addedFrom = searchParams.get("addedFrom") || "";
  const addedTo = searchParams.get("addedTo") || "";
  const activeStatus = searchParams.get("activeStatus") || "";
  const city = searchParams.get("city") || "";
  const county = searchParams.get("county") || "";

  // Filter and search the data client-side (same logic as desktop)
  const filteredData = useMemo(() => {
    if (!allHomesData) {
      return [];
    }
    
    const response = allHomesData as FilterSearchHomesResponse;
    if (!response.success || !response.data) {
      return [];
    }

    return response.data.filter((home: any) => {
      // Search filter (name, address, phone, or specific home ID)
      if (search) {
        const searchTrim = search.trim();
        const isNumericSearch = /^\d+$/.test(searchTrim);
        const searchAsNumber = isNumericSearch ? parseInt(searchTrim, 10) : 0;
        const treatAsHomeIdOnly = isNumericSearch && searchAsNumber > 1000;
        if (treatAsHomeIdOnly) {
          return home.id.toString() === searchTrim;
        }
        if (home.id.toString() === searchTrim) {
          return true;
        }
        const searchLower = searchTrim.toLowerCase();
        const name = home.homeowner?.user?.fullname?.toLowerCase() || "";
        const address = searchableAddressValue(home.address);
        const phone = home.homeowner?.user?.phone?.toLowerCase() || "";
        if (!name.includes(searchLower) &&
            !address.includes(searchLower) &&
            !phone.includes(searchLower)) {
          return false;
        }
      }

      // Filter by Active/Inactive (reported status)
      if (activeStatus) {
        const isActive = home.isReported === true;
        if (activeStatus === "active" && !isActive) return false;
        if (activeStatus === "inactive" && isActive) return false;
      }

      // Filter by city (multi-select; comma-separated in URL)
      if (city) {
        const cityList = city.split(',').map((c: string) => (c || '').trim().toLowerCase()).filter(Boolean);
        if (cityList.length > 0) {
          const homeCity = (home.city || '').trim().toLowerCase();
          if (!homeCity || !cityList.includes(homeCity)) return false;
        }
      }

      // Filter by county (multi-select; comma-separated in URL)
      if (county) {
        const countyList = county.split(',').map((c: string) => (c || '').trim().toLowerCase()).filter(Boolean);
        if (countyList.length > 0) {
          const homeCounty = (home.county || '').trim().toLowerCase();
          if (!homeCounty || !countyList.includes(homeCounty)) return false;
        }
      }

      // Filter by overall status (multi-select)
      if (overallStatus) {
        const statusArray = normalizeOverallStatusList(overallStatus.split(','));
        const homeStatus = normalizeOverallStatus(home.homeStatusInfo?.overallStatus);
        if (!statusArray.includes(homeStatus)) {
          return false;
        }
      }

      if (reliefStatus) {
        const statusArray = reliefStatus.split(',').map((s: string) => s.trim()).filter(Boolean);
        if (!statusArray.includes(home.reliefStatus ?? '')) return false;
      }
      if (rebuildingStatus) {
        const statusArray = rebuildingStatus.split(',').map((s: string) => s.trim()).filter(Boolean);
        if (!statusArray.includes(home.rebuildingStatus ?? '')) return false;
      }
      if (goodwillStatus) {
        const statusArray = goodwillStatus.split(',').map((s: string) => s.trim()).filter(Boolean);
        const homeGoodwill = home.goodwillStatus ?? "No Assistance Required";
        if (!statusArray.includes(homeGoodwill)) return false;
      }

      // Filter by Updated date range
      if (updatedFrom || updatedTo) {
        const u = home.updatedAt ? new Date(home.updatedAt).getTime() : null;
        if (u == null) return false;
        if (updatedFrom && /^\d{4}-\d{2}-\d{2}$/.test(updatedFrom)) {
          const fromStart = new Date(updatedFrom + "T00:00:00").getTime();
          if (u < fromStart) return false;
        }
        if (updatedTo && /^\d{4}-\d{2}-\d{2}$/.test(updatedTo)) {
          const toEnd = new Date(updatedTo + "T23:59:59.999").getTime();
          if (u > toEnd) return false;
        }
      }

      // Filter by Added date (when home was first added to DB)
      if (addedFrom || addedTo) {
        const created = home.createdAt ? new Date(home.createdAt).getTime() : null;
        if (created == null) return false;
        if (addedFrom && /^\d{4}-\d{2}-\d{2}$/.test(addedFrom)) {
          const fromStart = new Date(addedFrom + "T00:00:00").getTime();
          if (created < fromStart) return false;
        }
        if (addedTo && /^\d{4}-\d{2}-\d{2}$/.test(addedTo)) {
          const toEnd = new Date(addedTo + "T23:59:59.999").getTime();
          if (created > toEnd) return false;
        }
      }

      // Filter by roofing (multi-select)
      if (roofing) {
        const roofingArray = roofing.split(',');
        const homeRoofing = home.roofingInfo?.roofing;
        if (!roofingArray.includes(homeRoofing)) {
          return false;
        }
      }

      // Filter by kind of roof work (multi-select)
      if (kindOfRoofWork) {
        const roofWorkArray = kindOfRoofWork.split(',');
        const homeRoofWork = home.roofingInfo?.kindOfRoofWork;
        if (!roofWorkArray.includes(homeRoofWork)) {
          return false;
        }
      }

      // Filter by medical needs (any value = yes, empty/null/no = no)
      if (medicalNeeds) {
        const rawValue = home.homeowner?.medicalNeeds;
        const medicalNeedsValue = rawValue?.trim().toLowerCase();
        
        const hasMedicalNeeds = rawValue !== null && 
                               rawValue !== undefined && 
                               medicalNeedsValue && 
                               medicalNeedsValue !== '' && 
                               medicalNeedsValue !== 'no';
        
        const expectedHasMedicalNeeds = medicalNeeds === 'yes';
        
        if (hasMedicalNeeds !== expectedHasMedicalNeeds) {
          return false;
        }
      }

      // Filter by homeowner age (handle age ranges)
      if (homeownerAge && home.homeowner?.homeownerAge) {
        const actualAge = parseInt(home.homeowner.homeownerAge);
        const ageRange = homeownerAge.split(' - ');
        const minAge = parseInt(ageRange[0]);
        const maxAge = parseInt(ageRange[1]);
        
        if (actualAge < minAge || actualAge > maxAge) {
          return false;
        }
      }

      // Filter by single parent
      if (isSingleParent && home.homeowner?.isSingleParent !== isSingleParent) {
        return false;
      }

      // Filter by children under 18 (any number > 0 = yes, 0/null = no)
      if (numChildrenUnder18) {
        const actualChildren = home.homeowner?.numChildrenUnder18;
        const hasChildren = actualChildren !== null && 
                           actualChildren !== undefined && 
                           Number(actualChildren) > 0;
        const expectedHasChildren = numChildrenUnder18 === 'yes';
        
        if (hasChildren !== expectedHasChildren) {
          return false;
        }
      }

      // Filter by first responder
      if (isFirstResponder && home.homeowner?.isFirstResponder !== isFirstResponder) {
        return false;
      }

      // Filter by veteran
      if (isVeteran && home.homeowner?.isVeteran !== isVeteran) {
        return false;
      }


      // Filter by resident type (multi-select)
      if (residenceType) {
        const residenceTypeArray = residenceType.split(',');
        const homeResidenceType = home.repairabilityInfo?.residenceType;
        if (!homeResidenceType || !residenceTypeArray.includes(homeResidenceType)) {
          return false;
        }
      }

      // Filter by structure type (multi-select)
      if (structureType) {
        const structureTypeArray = structureType.split(',');
        const homeStructureType = home.repairabilityInfo?.structureType;
        if (!homeStructureType || !structureTypeArray.includes(homeStructureType)) {
          return false;
        }
      }

      // Filter by assessment completed (multi-select)
      if (assessmentCompleted) {
        const assessmentArray = assessmentCompleted.split(',');
        const homeAssessment = home.repairabilityInfo?.assessmentCompleted;
        if (!assessmentArray.includes(homeAssessment)) {
          return false;
        }
      }

      return true;
    });
  }, [allHomesData, search, activeStatus, city, county, overallStatus, reliefStatus, rebuildingStatus, goodwillStatus, roofing, kindOfRoofWork, medicalNeeds, homeownerAge, isSingleParent, numChildrenUnder18, isFirstResponder, isVeteran, residenceType, structureType, assessmentCompleted, updatedFrom, updatedTo, addedFrom, addedTo]);

  // Expose filtered data for Download when on small view (desktop table not mounted)
  useEffect(() => {
    setFilteredHomesForExport(filteredData);
    return () => setFilteredHomesForExport(null);
  }, [filteredData, setFilteredHomesForExport]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Update pagination context - MUST be called before any early returns
  useEffect(() => {
    setPaginationData({
      page: currentPage,
      limit: pageSize,
      total: totalItems
    });
  }, [currentPage, pageSize, totalItems, setPaginationData]);

  // Combine loading states - show loading if region is loading OR data is loading
  const isLoadingData = regionLoading || isLoading

  if (isLoadingData) {
    return (
      <div className="block xl:hidden space-y-3">
        <div className="text-sm text-muted-foreground">Loading…</div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="border rounded p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
              <div className="h-3 bg-muted rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="block xl:hidden space-y-3">
        <div>Error loading homeowners: {error.message}</div>
      </div>
    );
  }

  if (!allHomesData) {
    return (
      <div className="block xl:hidden space-y-3">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="block xl:hidden space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <HomeOwnersResultsCount total={totalItems} isLoading={isLoadingData} />
        <HomeOwnersFilterIndicator />
      </div>
      <div className="space-y-3">
        {paginatedData.map((home: any, index: number) => {
          const homeId = home.id;
          const homeowner = home.homeowner;

          return (
            <div key={home.id ?? index} className="border p-4 space-y-4 rounded">
              <div className="grid grid-cols-2 gap-x-4 sm:gap-x-5">
                <OwnerInfoMobile
                  infoTitle="Homeowner"
                  infoValue={
                    homeowner?.user?.fullname
                      ? shouldMaskHomeownerData(session?.role, session?.sub, homeowner?.user?.id)
                        ? maskHomeownerLastName(homeowner.user.fullname)
                        : homeowner.user.fullname
                      : "N/A"
                  }
                  href={homeowner?.id ? buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(homeowner.id)) : undefined}
                />
                <OwnerInfoMobile
                  infoTitle="Phone Number"
                  infoValue={
                    shouldMaskHomeownerData(session?.role, session?.sub, homeowner?.user?.id)
                      ? maskHomeownerPhone(homeowner?.user?.phone)
                      : (homeowner?.user?.phone ?? "N/A")
                  }
                  href={shouldMaskHomeownerData(session?.role, session?.sub, homeowner?.user?.id) ? undefined : (getPhoneTelLink(homeowner?.user?.phone) ?? undefined)}
                />
              </div>

              <div className="grid grid-cols-2 gap-x-4 sm:gap-x-5">
                <OwnerInfoMobile
                  infoTitle="Street Address"
                  infoValue={
                    isUnknownLocationValue(home.address)
                      ? getDisplayAddressValue(home.address)
                      : shouldMaskHomeownerData(session?.role, session?.sub, homeowner?.user?.id)
                      ? maskHomeownerAddress(home.address)
                      : (getDisplayAddressValue(home.address) || "No address available")
                  }
                  href={isUnknownLocationValue(home.address) ? undefined : buildMapLink(effectiveMapRoute, homeId)}
                />
                <OwnerInfoMobile
                  infoTitle="Updated"
                  infoValue={home.updatedAt ? new Date(home.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                />
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                    {homeowner?.id && (
                      <Link href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(homeowner.id))} className="min-w-0 flex-shrink">
                        <Button
                          variant="default"
                          size="sm"
                          className={`${getStatusButtonClass(home.homeStatusInfo?.overallStatus)} font-bold max-w-full truncate`}
                        >
                          {compactStatusLabelForHomeownersList(
                            displayOverallStatusLabel(home.homeStatusInfo?.overallStatus) || "—"
                          )}
                        </Button>
                      </Link>
                    )}
                    {!hideOwnerDetails && homeowner?.id && (
                      <Link href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(homeowner.id))} className="hidden sm:inline-block flex-shrink-0">
                        <Button variant="default" size="sm" className="bg-primary hover:bg-primary/90 text-white font-bold">
                          Home Details
                        </Button>
                      </Link>
                    )}
                    <span className="hidden sm:inline-flex">
                      {session?.role === UserRole.ADMIN && homeId && (
                        <ReportedStatusToggleSwitch
                          homeId={homeId}
                          currentStatus={home.isReported || false}
                          onStatusChange={handleReportedStatusChange}
                        />
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-x-1 flex-shrink-0">
                    <Link href={buildMapLink(effectiveMapRoute, homeId)} className="flex-shrink-0">
                      <Button variant="default" size="icon" className="rounded-md bg-foreground hover:bg-foreground/90 text-white w-7 h-7 flex-shrink-0">
                        <MapPin className="w-4 text-white" />
                      </Button>
                    </Link>
                    {!hideFlowchartLinks && (
                      <Link
                        href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(homeowner.id, { tab: 'workflow' }))}
                        className="flex-shrink-0"
                      >
                        <Button variant="default" size="icon" className="rounded-md bg-foreground hover:bg-foreground/90 text-white w-7 h-7 flex-shrink-0 p-0">
                          <Image src={buttonIcon} alt="Button" width={16} height={16} className="w-4 h-4" />
                        </Button>
                      </Link>
                    )}
                    <DeleteHomeownerDialog homeowner={homeowner} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <HomeOwnersTablePagination />
    </div>
  );
}

export default HomeOwnersCardsMobile;